import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import tinycolor from 'tinycolor2';
import store from '../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';
import DataTable from '../core/charts/Datatable';
import dataBoard from '../core/databoard';
import filters from '../core/filters';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import translate from '../core/tools/translate';
import environment from '../environment';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';

// import 'mapbox.js';

export class RetentionControl extends CoreControl {
    constructor () {
        super({ name: 'Retention' });

        this._retention = new Map();
        this._change = this._change.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this._purgeLayer = this._purgeLayer.bind(this);
        this._updateFilter = this._updateFilter.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);
    }

    static getInstance () {
        if (!RetentionControl._instance) {
            RetentionControl._instance = new RetentionControl();
        }

        return RetentionControl._instance;
    }

    init () {
        const values = {
            disabled: true,
            help: translate(`Help.${this.options.name}`),
            id: 'layer-retention',
            type: 'vector-points',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            purge: this._purgeLayer,
            overview: this.toggleOverview,
            filter: this.createFilter,
            dropdown: [],
            checkbox: {}
        };

        values.links = {};
        values.links[translate(`Action.ShowDateFrom`)] = {
            id: 'layer-retention-date',
            name: 'filterYear',
            text: `${moment(this.getDate().start).format('MMM/YYYY')} -> ${moment(this.getDate().end).format('MMM/YYYY')}`,
            value: {
                start: this.getDate().start.toDate(),
                end: this.getDate().end.toDate(),
                period: 4,
                ...this.periodLimits
            },
            change: this._changePeriod,
            type: 'datepicker',
            dataSource: [
                { text: 'Acumulado', value: 0 },
                { text: 'Semestral', value: 1 },
                { text: 'Trimestral', value: 2 },
                { text: 'Bimestral', value: 3 },
                { text: 'Mensal', value: 4 }
            ]
        };

        // values.checkbox[translate('Action.ShowOverview')] = {
        //     name: 'overviewFlag',
        //     change: this.toggleOverview,
        //     value: ''
        // };

        values.dropdown.push({
            name: 'category',
            custom: false,
            dataSource: [
                { text: translate('Retention.Retain'), value: 'retain' },
                { text: translate('Retention.Spread'), value: 'spread' }
            ],
            change: this._updateFilter
        });

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    get periodLimits(){
        return {
            end: environment.get('CONFIG.FILTERS.RETENTION_ENDDATE'),
            min: environment.get('CONFIG.FILTERS.RETENTION_STARTDATE')
        };
    }

    _updateFilter () {
        this.setLayerLegendSubtitle();
        this._filterCallback(filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getControls().dropdown.category.input.value()}`));
    }

    createFilter () {
        return filters.create({
            id: `filter-${_.kebabCase(this.options.name)}-${this.getControls().dropdown.category.input.value()}`,
            label: translate(`Common.${this.options.name}`),
            type: 'retention',
            direction: 'up',
            onSubmit: this._filterCallback
        });
    }

    _filterCallback (values) {
        let filter;
        if (!_.size(values)) {
            filter = {
                properties: {
                    type: _.map(this.getTypes(), r => r.type),
                    revisionKM: '10KM'
                }
            };
        } else {
            filter = {
                properties: _.reduce(values, (acc, cur, key) => {
                    acc[key] = cur.value;
                    return acc;
                }, {})
            };
        }

        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);

        retention.active.filters = filter.properties;
        this._setFilterProperties(filter);

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    toggleOverview () {
        const currentMap = worldMap.getCurrent();
        const features = this.getLayer(currentMap._id).getFeatures();

        if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    refreshPeriods(date, id = this.getLayer().getMap()._id) {
        this.date.set(id, {
            start: moment(date.start),
            end: moment(date.end),
            ...this.periodLimits
        });

        this.setLayerLegendSubtitle();

        const currentMap = worldMap.getCurrent();
        return this._getCurrentRetention(currentMap._id);
    }

    _changePeriod (date, id = this.getLayer().getMap()._id) {
        const retention = this.refreshPeriods(date, id);
        this.add(retention.active.feature, retention.active.store);
    }

    refreshControl(date){
        this._changePeriod(date);
    }

    get layerPeriod(){
        const id = this.getLayer().getMap()._id;
        return (this: any).date.get(id);
    }

    _setFilterProperties (filter = { properties: {} }) {
        filter.properties.status = 1;
        this.getLayer().setOptions({ filter });
    }

    setLayerLegendSubtitle () {
        const currentMap = worldMap.getCurrent();
        const date = this.date.get(currentMap._id);

        const legend = this.getLayer().options.legend;
        legend.title = this.getControls().dropdown.category.input.text();
        legend.subtitle = `${date.start.format('MM/YYYY')} - ${date.end.format('MM/YYYY')}`;
        this.getLayer().setOptions({ legend });
    }

    _getCurrentRetention () {
        const currentMap = worldMap.getCurrent();

        if (!this._retention.has(currentMap._id)) {
            this._retention.set(currentMap._id, {
                types: [{
                    id: 1,
                    type: 'OwnRetention',
                    color: '#3498DB',
                    category: ['retain', 'spread']
                }, {
                    id: 2,
                    type: 'GroupSpreading',
                    color: '#9B59B6',
                    category: ['spread']
                }, {
                    id: 3,
                    type: 'BrandSpreading',
                    color: '#f39c12',
                    category: ['spread']
                }, {
                    id: 4,
                    type: 'NoRetention',
                    color: '#E74C3C',
                    category: ['spread']
                }, {
                    id: 5,
                    type: 'GroupRetention',
                    color: '#9B59B6',
                    category: ['retain']
                }, {
                    id: 6,
                    type: 'BrandRetention',
                    color: '#1ABC9C',
                    category: ['retain']
                }],
                active: {}
            });
        }

        return this._retention.get(currentMap._id);
    }

    _change (e) {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);

        if (e.checked) {
            this.setLayerLegendSubtitle();
            this._setFilterProperties();
            if (retention.active.id) {
                this.add(retention.active.feature, retention.active.store);
            }
            this._setFilterButtonVisibility(true);
        } else {
            this._setFilterButtonVisibility(false);
            this.getLayer().hide();
            retention.active.store.restoreIcon(retention.active.id);
            retention.active = {};
        }
    }

    _setFilterButtonVisibility (state) {
        try {
            this.getControls().button.filter.input.enable(state);
            this.getControls().button.filter.enable = state;
        } catch (err) {
            console.log(err);
        }

        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                dataBoard.hide();
            }
        } catch (err) {
            console.log(err);
        }

        if (!state) {
            filters.close();
        }
    }

    _purgeLayer () {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);
        this.remove(retention.active.feature, retention.active.store);
        retention.active = {};
    }

    async _getIconStyle () {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);

        return {
            store: L.icon({
                iconUrl: `${environment.get('IMAGES_HOST')}/pin_store_retention.png`,
                iconSize: [50, 64], // size of the icon
                iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                labelAnchor: [25, -32]
            }),
            point: {
                type: 'uniq',
                property: 'type',
                values: await Promise.all(_.map(retention.types, async (type, i) => ({
                    value: type.type,
                    color: type.color,
                    style: {
                        icon: await pinBuilder.create.circle.small({ color: type.color, width: 13, height: 13 }),
                        clickable: false
                    }
                })))
            }
        };
    }

    summary (features) {
        const data = this.calculate(features);

        const table = new DataTable([{
            Indicador: translate('Retention.Report.SalesValue'),
            Valor: numeral(data.summary[0].value).format('0,0')
        }, {
            Indicador: (this.getControls().dropdown.category.input.value() === 'retain') ? translate('Retention.Report.RepairValue') : translate('Retention.Report.NoRetention'),
            Valor: numeral(data.summary[1].value).format('0,0')
        }].concat(
            _(data.aggregate)
                .map(d => (
                    [{
                        Indicador: translate(`Retention.Report.${d.type}Value`),
                        Valor: numeral(d.value).format('0,0')
                    }, {
                        Indicador: translate(`Retention.Report.${d.type}Percent`),
                        Valor: numeral(d.percent).format('0.00%')
                    }]
                ))
                .flatten()
                .value()
        ), {
            row: 'Indicador',
            column: ['Valor']
        });

        const chartPie = new DataTable(_.map(data.aggregate, d =>
            ({ Indicador: d.text, Valor: d.value })
        ), {
            row: 'Indicador',
            column: ['Valor']
        });

        const chartColumn = new DataTable(
            _(data.history)
                .groupBy('text')
                .map((v, k) => {
                    if (this.getControls().dropdown.category.input.value() === 'retain') {
                        return {
                            [translate('Common.Month')]: v[0].text,
                            // 'Tipo': translate(k).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, ''),
                            [translate('Common.TotalCustomerService')]: _.sumBy(v, t => t.value)
                        };
                    } else {
                        return {
                            [translate('Common.Month')]: v[0].text,
                            // 'Tipo': translate(k).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, ''),
                            'Não Retorno': _.sumBy(v, t => t.value)
                        };
                    }
                }).value()
            , {
                row: translate('Common.Month'),
                column: (() => {
                    if (this.getControls().dropdown.category.input.value() === 'retain') { return [translate('Common.TotalCustomerService')]; } else { return ['Não Retorno']; }
                })()
            });

        const options = {
            title: `${this.getStore().feature.properties.Nome} - ${this.getStore().feature.properties.Grupo} - Sumário`,
            dash: true,
            colors: [_.map(data.aggregate, d => d.color), (() => {
                let color;
                if (this.getControls().dropdown.category.input.value() === 'spread') {
                    color = tinycolor('#e74c3c');
                } else {
                    color = tinycolor('#3498db');
                }
                return _.map(_.range(0, 24), (i) => color.toString());
            })()],
            data: [table, chartPie, chartColumn],
            limit: chartColumn.getTotalRows(),
            startDate: moment(this.getDate().start).format('YYYYMM'),
            endDate: moment(this.getDate().end).format('YYYYMM')
        };

        dataBoard.show(options);
    }

    calculate (features) {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);
        const filterId = `filter-${_.kebabCase(this.options.name)}-${this.getControls().dropdown.category.input.value()}`;

        const types = _(retention.types)
            .filter(t => _.some(t.category, c => c === this.getControls().dropdown.category.input.value()))
            .value();

        const filter = _.cloneDeep(retention.active.filters || this.getLayer().options.filter.properties);
        filter.type = _.map(types, t => t.type);

        if (filters.has(filterId)) {
            const revisionKM = filters.get(filterId).revisionKM.value;
            if (_.size(revisionKM)) {
                filter.revisionKM = revisionKM;
            } else {
                delete filter.revisionKM;
            }
        } else {
            filter.revisionKM = '10KM';
        }

        const doc = this._filterFeatures(features, filter);

        delete filter.type;

        const summary = [{
            value: _(this._filterFeatures(features, filter)).filter(f => _.some(['OwnRetention', 'GroupSpreading', 'BrandSpreading', 'NoRetention'], r => f.properties.type === r)).size(),
            text: 'Veículos Vendidos'
        }];

        if (this.getControls().dropdown.category.input.value() === 'retain') {
            summary.push({
                value: _.size(doc),
                text: 'Veículos Atendidos'
            });
        } else {
            summary.push({
                value: _(doc).filter(f => _.some(['NoRetention', 'GroupSpreading', 'BrandSpreading'], r => f.properties.type === r)).size(),
                text: 'Não Retenção'
            });
        }

        const address = _.reduce(['InvalidPosition', 'ValidPosition'], (res, val, index) => {
            res.push({
                text: translate(`Retention.${val}`),
                value: _(doc).filter(f => f.properties.status === index).size()
            });
            return res;
        }, []);

        const aggregate = (() => {
            const data = _(doc)
                .filter(f => _.some(types, t => t.type === f.properties.type))
                .groupBy('properties.type')
                .map((val, key) =>
                    ({
                        id: _.find(types, t => t.type === key).id,
                        value: _.size(val),
                        type: key,
                        text: translate(`Retention.Classification.${key}`).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, '')
                    })
                ).value();

            return _(types)
                .map((t) => {
                    const out = _.find(data, d => d.id === t.id);

                    if (out) {
                        const total = summary[0].value || summary[1].value;

                        switch (t.id) {
                            case 5:
                                out.percent = _(data).filter(d => _.some([1, 5], v => d.id === v)).sumBy('value') / total;
                                break;
                            case 6:
                                out.percent = _(data).filter(d => _.some([1, 5, 6], v => d.id === v)).sumBy('value') / total;
                                break;
                            default:
                                out.percent = _(data).filter(d => _.some([t.id], v => d.id === v)).sumBy('value') / total;
                        }

                        out.percent = parseFloat(out.percent.toFixed(4));
                        out.color = t.color;
                        return out;
                    }

                    return ({
                        text: translate(`Retention.Classification.${t.type}`).replace(/Atendidos ((de)?(do)? )?/, '').replace(/Retorno ((em)?(no)? )?/, ''),
                        value: 0,
                        percent: 0,
                        color: t.color,
                        type: t.type
                    });
                })
                .value();
        })();

        const history = _(doc)
            .filter((f) => {
                if (this.getControls().dropdown.category.input.value() === 'retain') {
                    return _.some(types, t => t.type === f.properties.type);
                }
                return _.some(['NoRetention'], t => f.properties.type === t);
            })
            .orderBy(f => f.properties.revisionDate)
            .groupBy(f => moment(f.properties.revisionDate).format('YYYYMM'))
            .map((val, key) =>
                _(val)
                    .groupBy(f => f.properties.type)
                    .map((v, k) => ({
                        type: k,
                        value: _.size(v),
                        text: moment(key, 'YYYYMM').format('MMM/YY')
                    }))
                    .value()
            )
            .flatten()
            .value();

        return {
            address,
            aggregate,
            history,
            summary
        };
    }

    _filterFeatures (features, properties) {
        return _.filter(features, f =>
            _.every(properties, (val, key) => {
                if (key === 'status') return true;
                const value = String(f.properties[key]).toLowerCase();
                if (_.isArray(val) && _.some(val, v => value === String(v).toLowerCase())) {
                    return true;
                } else if (value === String(val).toLowerCase()) {
                    return true;
                }
                return false;
            })
        );
    }

    getTypes () {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);
        let category;

        try {
            category = this.getControls().dropdown.category.input.value();
        } catch (e) {
            category = 'retain';
        }

        return _.filter(retention.types, r => _.some(r.category, c => String(c) === String(category)));
    }

    getDate (id = this.getLayer().getMap()._id) {
        if (_.isUndefined(this.date)) {
            this.date = new Map();
        }

        if (!this.date.has(id)) {
            this.date.set(id, id == 0 ? {   start: moment(environment.get('CONFIG.FILTERS.RETENTION_ENDDATE')).startOf('month'),
                    end: moment(environment.get('CONFIG.FILTERS.RETENTION_ENDDATE')).endOf('month')}
                : this._date.get((id - 1)));
        }

        return this.date.get(id);
    }

    getStore () {
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);
        const { id, store } = retention.active;
        if (store) {
            return store.getLayer(id);
        }

        return undefined;
    }

    add (feature, store) {
        if (!feature || !store) {
            console.log('ERROR: Feature or Store not available');
            return;
        }

        const id = feature.properties.Id;
        const storeClientId = feature.properties.IdLojaCliente;
        const currentMap = worldMap.getCurrent();
        const retention = this._getCurrentRetention(currentMap._id);
        this.remove(retention.active.feature, retention.active.store);

        retention.active = { id, store, feature };

        this._getIconStyle().then((style) => {
            // change pin from the selected store
            store.changeIcon(id, style.store);

            const date = this.getDate(currentMap._id);
            const symbology = _.assign({}, this.getLayer().options.symbology, style.point);
            const options = {
                remote: {
                    params: {
                        IdLoja: storeClientId,
                        AnoMesInicio: date.start.format('YYYYMM'),
                        AnoMesFim: date.end.format('YYYYMM')
                    }
                },
                filter: {
                    properties: {
                        type: _.map(this.getTypes(), r => r.type),
                        revisionKM: '10KM',
                        status: 1
                    }
                },
                symbology
            };
            this.getLayer().setOptions(options);

            this.getLayer().redraw();
        });
    }

    remove (feature, store) {
        if (!feature || !store) return;

        try {
            const id = feature.properties.Id;

            store.restoreIcon(id);
            this.getLayer().clear();
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }
}

export default RetentionControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/RetentionControl.js